import React, {
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  useServiceGroupsQuery,
  useReorderServicesMutation,
  useReorderServiceGroupsMutation,
  useDeleteServiceGroupMutation,
  ServiceGroupDataFragment,
  ProviderService,
} from '../../../generated/graphql';
import SortableServiceGroup from './SortableServiceGroup';
import ServiceCard from '../ServiceCard';
import { useAuth } from '../../../contexts/AuthContext';
import { customToast } from '../ToastAlert/customToast';
import classNames from 'classnames';

interface SortableServicesModuleProps {
  className?: string;
  handleDeleteService?: (service: ProviderService) => void;
}

const SortableServicesModule: React.FC<SortableServicesModuleProps> = ({
  className,
  handleDeleteService,
}) => {
  const { authedProviderUser } = useAuth();
  const { data: serviceGroupsData, refetch: refetchServiceGroups } =
    useServiceGroupsQuery();
  const [reorderServices] = useReorderServicesMutation();
  const [reorderServiceGroups] = useReorderServiceGroupsMutation();
  const [deleteServiceGroup] = useDeleteServiceGroupMutation();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<'service' | 'group' | null>(
    null,
  );
  const [overId, setOverId] = useState<string | null>(null);

  const successToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Configure sensor to require press-and-hold (150ms) or movement (8px) before drag starts
  // This allows clicks to navigate while press-and-hold enables dragging
  // Also include KeyboardSensor for accessibility (keyboard navigation)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 150, // Require 150ms press before drag activates
        tolerance: 8, // Or 8px of movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const serviceGroupsFromQuery = serviceGroupsData?.serviceGroups || [];

  // Local state for optimistic updates
  const [localGroups, setLocalGroups] = useState<ServiceGroupDataFragment[]>(
    serviceGroupsFromQuery,
  );
  const isOptimisticUpdateRef = useRef(false);

  // Sync local state with query data when it changes
  // Skip syncing if we're in the middle of an optimistic update (drag operations)
  useEffect(() => {
    // Only skip syncing if we're actively in an optimistic update
    // This allows refetches (like after creating a group) to sync properly
    if (!isOptimisticUpdateRef.current) {
      setLocalGroups(serviceGroupsFromQuery);
    }
    // Always reset the flag after effect runs to allow next update to sync
    // This ensures that after a drag operation completes, new refetches will sync
    isOptimisticUpdateRef.current = false;
  }, [serviceGroupsFromQuery]);

  const serviceGroups = localGroups;

  const showSuccessFeedback = (message: string) => {
    // Clear any pending success toast
    if (successToastTimeoutRef.current) {
      clearTimeout(successToastTimeoutRef.current);
    }

    // Debounce: only show after 500ms of no more changes
    successToastTimeoutRef.current = setTimeout(() => {
      customToast.success(message);
      successToastTimeoutRef.current = null;
    }, 500);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    setOverId(null);

    // Determine if dragging a service or group
    const isGroup = serviceGroups.some((g) => g.id === active.id);
    setActiveType(isGroup ? 'group' : 'service');
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over ? (over.id as string) : null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setOverId(null);

    if (!over || active.id === over.id) {
      setActiveId(null);
      setActiveType(null);
      return;
    }

    const activeIdStr = active.id as string;
    const overIdStr = over.id as string;

    // Check if dragging a group
    const activeGroup = serviceGroups.find((g) => g.id === activeIdStr);
    if (activeGroup) {
      await handleGroupReorder(activeGroup, overIdStr);
      setActiveId(null);
      setActiveType(null);
      return;
    }

    // Otherwise, it's a service being dragged
    await handleServiceReorder(activeIdStr, overIdStr);
    setActiveId(null);
    setActiveType(null);
  };

  const handleGroupReorder = async (
    draggedGroup: ServiceGroupDataFragment,
    overId: string,
  ) => {
    const overGroup = serviceGroups.find((g) => g.id === overId);
    if (!overGroup) return;

    const oldIndex = serviceGroups.findIndex((g) => g.id === draggedGroup.id);
    const newIndex = serviceGroups.findIndex((g) => g.id === overGroup.id);

    if (oldIndex === newIndex) return;

    // Optimistically update local state
    isOptimisticUpdateRef.current = true;
    const reorderedGroups = arrayMove(serviceGroups, oldIndex, newIndex);
    setLocalGroups(reorderedGroups);

    const input = reorderedGroups.map((group, index) => ({
      serviceGroupId: group.id,
      displayOrder: index,
    }));

    try {
      await reorderServiceGroups({ variables: { input } });
      isOptimisticUpdateRef.current = false;
      await refetchServiceGroups();
      showSuccessFeedback('Groups reordered');
    } catch (error) {
      console.error('Failed to reorder groups:', error);
      // Revert on error
      isOptimisticUpdateRef.current = false;
      setLocalGroups(serviceGroupsFromQuery);
      customToast.error('Failed to reorder groups');
    }
  };

  // Type assertion helper - the services from the query are compatible
  const getService = (service: any): ProviderService =>
    service as ProviderService;

  const reorderServicesInGroup = async (
    draggedService: ProviderService,
    groupId: string,
    targetIndex: number,
  ) => {
    const group = serviceGroups.find((g) => g.id === groupId);
    if (!group) return;

    const currentIndex =
      group.services?.findIndex((s) => s.id === draggedService.id) ?? -1;
    if (currentIndex === -1 || currentIndex === targetIndex) return;

    // Optimistically update local state
    isOptimisticUpdateRef.current = true;
    const services = [...(group.services || []).map(getService)];
    const reorderedServices = arrayMove(services, currentIndex, targetIndex);

    const updatedGroups = serviceGroups.map((g) => {
      if (g.id === groupId) {
        return {
          ...g,
          services: reorderedServices,
        };
      }
      return g;
    });
    setLocalGroups(updatedGroups);

    // Build reorder input - update ALL services in the group
    const input = reorderedServices.map((s, index) => ({
      serviceId: s.id,
      serviceGroupId: groupId,
      displayOrder: index,
    }));

    try {
      await reorderServices({ variables: { input } });
      isOptimisticUpdateRef.current = false;
      await refetchServiceGroups();
      showSuccessFeedback('Services reordered');
    } catch (error) {
      console.error('Failed to reorder services:', error);
      // Revert on error
      isOptimisticUpdateRef.current = false;
      setLocalGroups(serviceGroupsFromQuery);
      customToast.error('Failed to reorder services');
    }
  };

  const handleServiceReorder = async (activeId: string, overId: string) => {
    // Find the service being dragged
    let draggedService: ProviderService | null = null;
    let sourceGroupId: string | null = null;

    for (const group of serviceGroups) {
      const index = group.services?.findIndex((s) => s.id === activeId) ?? -1;
      if (index !== -1 && group.services?.[index]) {
        draggedService = getService(group.services[index]);
        sourceGroupId = group.id;
        break;
      }
    }

    if (!draggedService) return;

    // Determine target
    const targetGroup = serviceGroups.find((g) => g.id === overId);
    const targetService = serviceGroups
      .flatMap((g) => g.services || [])
      .find((s) => s.id === overId);

    if (targetGroup) {
      // Dropping into a group
      await moveServiceToGroup(
        draggedService,
        sourceGroupId,
        targetGroup.id,
        targetGroup.services?.length || 0,
      );
    } else if (targetService) {
      // Dropping on another service
      const targetGroupId = serviceGroups.find((g) =>
        g.services?.some((s) => s.id === targetService.id),
      )?.id;

      if (targetGroupId) {
        const targetIndex =
          serviceGroups
            .find((g) => g.id === targetGroupId)
            ?.services?.findIndex((s) => s.id === targetService.id) ?? -1;

        if (targetIndex === -1) return;

        // Check if moving within the same group
        if (sourceGroupId === targetGroupId) {
          // Reordering within the same group
          await reorderServicesInGroup(
            draggedService,
            sourceGroupId,
            targetIndex,
          );
        } else {
          // Moving between groups
          await moveServiceToGroup(
            draggedService,
            sourceGroupId,
            targetGroupId,
            targetIndex,
          );
        }
      }
    }
  };

  const moveServiceToGroup = async (
    service: ProviderService,
    sourceGroupId: string | null,
    targetGroupId: string,
    targetIndex: number,
  ) => {
    const sourceGroup = serviceGroups.find((g) => g.id === sourceGroupId);
    const targetGroup = serviceGroups.find((g) => g.id === targetGroupId);

    if (!targetGroup) return;

    // Optimistically update local state
    isOptimisticUpdateRef.current = true;
    const updatedGroups = serviceGroups.map((group) => {
      if (group.id === sourceGroupId) {
        // Remove service from source group
        return {
          ...group,
          services: (group.services || []).filter((s) => s.id !== service.id),
        };
      }
      if (group.id === targetGroupId) {
        // Add service to target group at the right position
        const targetServices = [...(group.services || []).map(getService)];
        targetServices.splice(targetIndex, 0, service);
        return {
          ...group,
          services: targetServices,
        };
      }
      return group;
    });
    setLocalGroups(updatedGroups);

    // Remove from source group
    const sourceServices =
      sourceGroup?.services
        ?.filter((s) => s.id !== service.id)
        .map(getService) || [];

    // Add to target group
    const targetServices = [...(targetGroup.services || []).map(getService)];
    targetServices.splice(targetIndex, 0, service);

    // Build reorder input
    const input: Array<{
      serviceId: string;
      serviceGroupId: string | null;
      displayOrder: number;
    }> = [];

    // Update source group services
    sourceServices.forEach((s, index) => {
      input.push({
        serviceId: s.id,
        serviceGroupId: sourceGroupId,
        displayOrder: index,
      });
    });

    // Update target group services
    targetServices.forEach((s, index) => {
      input.push({
        serviceId: s.id,
        serviceGroupId: targetGroupId,
        displayOrder: index,
      });
    });

    try {
      await reorderServices({ variables: { input } });
      isOptimisticUpdateRef.current = false;
      await refetchServiceGroups();
      showSuccessFeedback('Services reordered');
    } catch (error) {
      console.error('Failed to reorder services:', error);
      // Revert on error
      isOptimisticUpdateRef.current = false;
      setLocalGroups(serviceGroupsFromQuery);
      customToast.error('Failed to reorder services');
    }
  };

  const activeService = useMemo(() => {
    if (activeType !== 'service' || !activeId) return null;
    const service = serviceGroups
      .flatMap((g) => g.services || [])
      .find((s) => s.id === activeId);
    return service ? (service as ProviderService) : null;
  }, [activeId, activeType, serviceGroups]);

  const activeGroup = useMemo(() => {
    if (activeType !== 'group' || !activeId) return null;
    return serviceGroups.find((g) => g.id === activeId);
  }, [activeId, activeType, serviceGroups]);

  const groupIds = serviceGroups.map((g) => g.id);

  // Use pointerWithin for pointer-based dragging - registers drop when cursor is within
  // the droppable bounds (fixes deadzones at edges of empty groups). Fall back to
  // closestCenter for keyboard sensor which doesn't have pointer coordinates.
  const collisionDetection = useCallback(
    (args: Parameters<typeof pointerWithin>[0]) => {
      const pointerCollisions = pointerWithin(args);
      if (pointerCollisions.length > 0) {
        return pointerCollisions;
      }
      return closestCenter(args);
    },
    [],
  );

  // Check if there are any services in any group
  const hasAnyServices = serviceGroups.some(
    (group) => group.services && group.services.length > 0,
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className={classNames('px-6 py-10', className)}>
          <SortableContext
            items={groupIds}
            strategy={verticalListSortingStrategy}
          >
            {serviceGroups.map((group) => {
              // Check if a service is being dragged over this group
              // This happens when:
              // 1. We're dragging a service (activeType === 'service')
              // 2. The over target is either the group itself OR a service within this group
              const isServiceDragOverGroup =
                activeType === 'service' &&
                (overId === group.id ||
                  group.services?.some((s) => s.id === overId));

              return (
                <SortableServiceGroup
                  key={group.id}
                  group={group as ServiceGroupDataFragment}
                  providerPrimaryColor={authedProviderUser?.primaryColor}
                  canDelete={serviceGroups.length > 1}
                  onDelete={async (groupId) => {
                    try {
                      await deleteServiceGroup({
                        variables: { serviceGroupId: groupId },
                      });
                      await refetchServiceGroups();
                    } catch (error: any) {
                      const errorMessage =
                        error?.message || 'Failed to delete group';
                      customToast.error(
                        errorMessage.includes('service(s)')
                          ? errorMessage
                          : 'Failed to delete group. Please remove all services first.',
                      );
                    }
                  }}
                  handleDeleteService={handleDeleteService}
                  hasAnyServices={hasAnyServices}
                  isServiceDragOver={isServiceDragOverGroup}
                />
              );
            })}
          </SortableContext>
        </div>

        <DragOverlay>
          {activeService ? (
            <div
              className="rotate-2 scale-105 shadow-2xl"
              style={{ opacity: 0.9 }}
            >
              <ServiceCard
                service={activeService}
                providerPrimaryColor={authedProviderUser?.primaryColor}
              />
            </div>
          ) : activeGroup ? (
            <div className="opacity-50">
              <div className="rounded-lg bg-white p-4 shadow-lg">
                <h3>{activeGroup.name}</h3>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
};

export default SortableServicesModule;
